/**
 * Utilitaires pour les composants GreenMiles
 */

// Import de la fonction de comparaison partagée depuis hotelUtils
import { compareRoomNumbers } from "../../HotelPage/utils/hotelUtils";

// Formatage des nombres avec décimales
export const formatNumber = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  return Number(value).toFixed(decimals);
};

// Formatage des nombres entiers (pour les compteurs, scans, etc.)
export const formatInteger = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0";
  return Math.round(Number(value)).toLocaleString("fr-FR");
};

// Formatage des pourcentages
export const formatPercentage = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0%";
  return `${Number(value).toFixed(1)}%`;
};

// Couleurs pour les métriques selon leur performance
export const getMetricColor = (value, thresholds = { good: 70, medium: 40 }) => {
  if (value >= thresholds.good) return "text-green-600";
  if (value >= thresholds.medium) return "text-yellow-600";
  return "text-red-600";
};

// Couleurs de fond pour les badges selon la performance
export const getMetricBadgeColor = (value, thresholds = { good: 70, medium: 40 }) => {
  if (value >= thresholds.good) return "bg-green-100 text-green-800";
  if (value >= thresholds.medium) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

// Couleur pour le taux d'engagement
export const getEngagementColor = (rate) => getMetricColor(rate, { good: 60, medium: 30 });

// Couleur pour les actions moyennes par utilisateur
export const getActionsPerUserColor = (actions) => getMetricColor(actions * 20, { good: 60, medium: 30 }); // Multiplication par 20 pour avoir un score sur 100

// Formatage de la date de création
export const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

// Formatage de la date et heure
export const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    return new Date(timestamp).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "N/A";
  }
};

// Tri des hôtels selon différents critères
export const sortGreenMilesHotels = (hotels, sortColumn, sortDirection) => {
  return [...hotels].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case "hName":
        aValue = a.hName?.toLowerCase() || "";
        bValue = b.hName?.toLowerCase() || "";
        break;
      case "hCity":
        aValue = a.hCity?.toLowerCase() || "";
        bValue = b.hCity?.toLowerCase() || "";
        break;
      case "qr_rooms_count":
        aValue = a.statistics?.qr_rooms_count || 0;
        bValue = b.statistics?.qr_rooms_count || 0;
        break;
      case "unique_users":
        aValue = a.statistics?.unique_users || 0;
        bValue = b.statistics?.unique_users || 0;
        break;
      case "avg_actions_per_user":
        aValue = a.statistics?.avg_actions_per_user || 0;
        bValue = b.statistics?.avg_actions_per_user || 0;
        break;
      case "avg_actions_per_active_user":
        aValue = a.statistics?.avg_actions_per_active_user || 0;
        bValue = b.statistics?.avg_actions_per_active_user || 0;
        break;
      case "avg_greenmiles_per_stay":
        aValue = a.statistics?.avg_greenmiles_per_stay || 0;
        bValue = b.statistics?.avg_greenmiles_per_stay || 0;
        break;
      case "weekly_actions_count":
        aValue = a.statistics?.weekly_actions_count || 0;
        bValue = b.statistics?.weekly_actions_count || 0;
        break;
      case "engagement_rate":
        aValue = a.statistics?.engagement_rate || 0;
        bValue = b.statistics?.engagement_rate || 0;
        break;
      default:
        aValue = a[sortColumn] || "";
        bValue = b[sortColumn] || "";
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }
  });
};

// Icônes de tri
export const getSortIcon = (column, currentColumn, currentDirection) => {
  if (column !== currentColumn) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  return currentDirection === "asc" ? (
    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
    </svg>
  );
};

// Obtenir la direction de tri suivante
export const getNextSortDirection = (currentColumn, newColumn, currentDirection) => {
  if (currentColumn !== newColumn) {
    return "asc";
  }
  return currentDirection === "asc" ? "desc" : "asc";
};

// Résumé des statistiques pour un hôtel
export const getHotelStatsSummary = (hotel) => {
  const stats = hotel.statistics || {};
  return {
    totalUsers: stats.unique_users || 0,
    totalStays: stats.total_stays || 0,
    totalActions: stats.total_actions_collected || 0,
    avgActionsPerUser: stats.avg_actions_per_user || 0,
    avgActionsPerActiveUser: stats.avg_actions_per_active_user || 0,
    avgGreenMiles: stats.avg_greenmiles_per_stay || 0,
    weeklyActions: stats.weekly_actions_count || 0,
    engagementRate: stats.engagement_rate || 0,
    qrRooms: stats.qr_rooms_count || 0,
    popularActions: hotel.popular_actions_week || [],
  };
};

// Validation des données d'hôtel
export const validateHotelData = (hotel) => {
  const errors = [];

  if (!hotel.hName) errors.push("Nom d'hôtel manquant");
  if (!hotel.hCity) errors.push("Ville manquante");
  if (hotel.hStars < 1 || hotel.hStars > 5) errors.push("Nombre d'étoiles invalide");

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ===== NOUVELLES FONCTIONS POUR LE TRI DES CHAMBRES QR =====

// Colonnes de tri disponibles pour les chambres QR
export const QR_SORT_COLUMNS = {
  ROOM_NUMBER: "rRoomNumber",
  ROOM_TYPE: "roomType",
  LS_STATUS: "lsStatus",
  QR_CODE: "qrId",
  LAST_STAY: "last_stay_date",
  LAST_STAY_STATUS: "last_stay_status",
};

const LS_STATUS_PRIORITY = {
  "Action Ready": 1,
  "Module Non-ready": 2,
  Aucun: 3,
};

const OPTION_EAU_STATUS_PRIORITY = {
  "Option Eau Ready": 1,
  Warning: 2,
  "Non éligible": 3,
};

/**
 * Trie les chambres QR selon la colonne et direction spécifiées
 */
export const sortQRRooms = (rooms, sortColumn, sortDirection) => {
  const sortedRooms = [...rooms].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case QR_SORT_COLUMNS.ROOM_NUMBER:
        // Tri intelligent des numéros de chambre
        return sortDirection === "asc" ? compareRoomNumbers(a.rRoomNumber, b.rRoomNumber) : compareRoomNumbers(b.rRoomNumber, a.rRoomNumber);

      case QR_SORT_COLUMNS.ROOM_TYPE:
        aValue = OPTION_EAU_STATUS_PRIORITY[a.optionEauStatus] || 99;
        bValue = OPTION_EAU_STATUS_PRIORITY[b.optionEauStatus] || 99;
        break;

      case QR_SORT_COLUMNS.QR_CODE:
        aValue = (a.qrId || "").toLowerCase();
        bValue = (b.qrId || "").toLowerCase();
        break;

      case QR_SORT_COLUMNS.LS_STATUS:
        aValue = LS_STATUS_PRIORITY[a.lsStatus] || 99;
        bValue = LS_STATUS_PRIORITY[b.lsStatus] || 99;
        break;

      case QR_SORT_COLUMNS.LAST_STAY:
        aValue = a.last_stay_date ? new Date(a.last_stay_date).getTime() : 0;
        bValue = b.last_stay_date ? new Date(b.last_stay_date).getTime() : 0;
        break;

      case QR_SORT_COLUMNS.LAST_STAY_STATUS:
        // Priorité des statuts pour le tri
        const stayStatusPriority = {
          IN_PROGRESS: 1,
          WAITING_FOR_GUEST: 2,
          STAND_BY: 3,
          FINISHED: 4,
          NO_SHOW: 5,
          CANCELLED: 6,
          DELIVERY_PREVIEW: 7,
        };
        aValue = stayStatusPriority[a.last_stay_status] || 99;
        bValue = stayStatusPriority[b.last_stay_status] || 99;
        // Si même priorité, trier par date
        if (aValue === bValue) {
          aValue = a.last_stay_date ? new Date(a.last_stay_date).getTime() : 0;
          bValue = b.last_stay_date ? new Date(b.last_stay_date).getTime() : 0;
        }
        break;

      default:
        return 0;
    }

    // Tri standard pour les valeurs numériques et textuelles
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return sortedRooms;
};

/**
 * Obtient la prochaine direction de tri
 */
export const getQRSortDirection = (currentColumn, newColumn, currentDirection) => {
  if (currentColumn !== newColumn) {
    return "asc";
  }
  return currentDirection === "asc" ? "desc" : "asc";
};

/**
 * Obtient l'icône de tri appropriée
 */
export const getQRSortIcon = (column, currentColumn, currentDirection) => {
  if (column !== currentColumn) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (currentDirection === "asc") {
    return (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    );
  }

  return (
    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
    </svg>
  );
};

/**
 * Formate la date de création du QR code
 */
export const formatQRDate = (timestamp) => {
  if (!timestamp) return "-";

  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    return "-";
  }
};

/**
 * Formate une date (DATE SQL) en format français
 */
export const formatStayDate = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    return "-";
  }
};

/**
 * Obtient le libellé français du statut de stay
 */
export const getStayStatusLabel = (status) => {
  const labels = {
    IN_PROGRESS: "Séjour GreenMiles",
    WAITING_FOR_GUEST: "Waiting for guest",
    STAND_BY: "En attente",
    FINISHED: "Terminé",
    NO_SHOW: "No show",
    CANCELLED: "Annulé",
    DELIVERY_PREVIEW: "Aperçu livraison",
    NO_STAY: "Aucun séjour",
  };
  return labels[status] || status || "-";
};

/**
 * Obtient le style pour le badge de statut de stay
 */
export const getStayStatusStyle = (status) => {
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: 500,
  };

  switch (status) {
    case "IN_PROGRESS":
      return {
        ...baseStyle,
        backgroundColor: "#C8FFC9",
        border: "1px solid #00D50E",
        color: "#0B3D0F",
      };
    case "WAITING_FOR_GUEST":
      return {
        ...baseStyle,
        backgroundColor: "#FFF4E6",
        border: "1px solid #FFA500",
        color: "#8B4513",
      };
    case "STAND_BY":
      return {
        ...baseStyle,
        backgroundColor: "#E6F3FF",
        border: "1px solid #4A90E2",
        color: "#1E3A5F",
      };
    case "FINISHED":
      return {
        ...baseStyle,
        backgroundColor: "#E8E8E8",
        border: "1px solid #888888",
        color: "#333333",
      };
    case "NO_SHOW":
      return {
        ...baseStyle,
        backgroundColor: "#FFE6E6",
        border: "1px solid #FF4444",
        color: "#8B0000",
      };
    case "DELIVERY_PREVIEW":
      return {
        ...baseStyle,
        backgroundColor: "#EDE9FE",
        border: "1px solid #7C3AED",
        color: "#4C1D95",
      };
    case "CANCELLED":
      return {
        ...baseStyle,
        backgroundColor: "#FFE6E6",
        border: "1px solid #FF4444",
        color: "#8B0000",
      };
    case "NO_STAY":
      return {
        ...baseStyle,
        backgroundColor: "#F3F4F6",
        border: "1px solid #9CA3AF",
        color: "#4B5563",
      };
    default:
      return {
        ...baseStyle,
        backgroundColor: "#FFFFFF",
        border: "1px solid #111827",
        color: "#111827",
      };
  }
};

// ===== FONCTIONS POUR LE FILTRAGE PAR STATUT DE STAY =====

/**
 * Liste complète de tous les statuts de stay possibles
 */
const ALL_POSSIBLE_STAY_STATUSES = ["IN_PROGRESS", "WAITING_FOR_GUEST", "STAND_BY", "FINISHED", "NO_SHOW", "CANCELLED", "DELIVERY_PREVIEW"];

/**
 * Obtient la liste des statuts de stay disponibles avec leurs compteurs
 * Affiche TOUS les statuts possibles, même ceux avec 0 chambre
 */
export const getAvailableStayStatuses = (rooms) => {
  // Compter les chambres par statut de stay
  const statusCounts = rooms.reduce((acc, room) => {
    const status = room.last_stay_status || "NO_STAY";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Créer la liste complète des statuts avec leurs informations
  const statuses = ALL_POSSIBLE_STAY_STATUSES.map((status) => ({
    value: status,
    label: getStayStatusLabel(status),
    count: statusCounts[status] || 0, // 0 si le statut n'existe pas
  }));

  // Ajouter le statut "NO_STAY" pour les chambres sans stay
  if (statusCounts["NO_STAY"]) {
    statuses.push({
      value: "NO_STAY",
      label: "Aucun séjour",
      count: statusCounts["NO_STAY"],
    });
  }

  // Trier par priorité (statuts actifs en premier)
  const statusPriority = {
    IN_PROGRESS: 1,
    WAITING_FOR_GUEST: 2,
    STAND_BY: 3,
    FINISHED: 4,
    NO_SHOW: 5,
    CANCELLED: 6,
    DELIVERY_PREVIEW: 7,
    NO_STAY: 8,
  };

  return statuses.sort((a, b) => {
    const priorityA = statusPriority[a.value] || 999;
    const priorityB = statusPriority[b.value] || 999;
    return priorityA - priorityB;
  });
};

/**
 * Filtre les chambres selon les statuts de stay sélectionnés
 */
export const filterRoomsBySelectedStayStatuses = (rooms, selectedStayStatuses) => {
  if (!selectedStayStatuses || selectedStayStatuses.length === 0) {
    return [];
  }

  return rooms.filter((room) => {
    const roomStatus = room.last_stay_status || "NO_STAY";
    return selectedStayStatuses.includes(roomStatus);
  });
};

/**
 * Obtient tous les statuts de stay disponibles (pour initialisation)
 * Retourne tous les statuts possibles, pas seulement ceux présents
 */
export const getAllStayStatusValues = (rooms) => {
  const statuses = new Set();
  rooms.forEach((room) => {
    const status = room.last_stay_status || "NO_STAY";
    statuses.add(status);
  });
  return Array.from(statuses);
};

// ===== NOUVELLES FONCTIONS POUR LE TRI DES ACTIONS =====

// Colonnes de tri disponibles pour les actions
export const ACTION_SORT_COLUMNS = {
  NAME: "display_name",
  TYPE: "acType",
  GREEN_MILES: "acGreenMile",
  USAGE_COUNT: "usageCount",
  UNIQUE_USERS: "uniqueUsers",
  TOTAL_GREENMILES: "totalGreenmiles",
  POSITION: "acPosition",
  VISIBLE: "acVisible",
};

/**
 * Trie les actions selon la colonne et direction spécifiées
 */
export const sortActions = (actions, sortColumn, sortDirection) => {
  const sortedActions = [...actions].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case ACTION_SORT_COLUMNS.NAME:
        aValue = (a.display_name || a.acTitle || "").toLowerCase();
        bValue = (b.display_name || b.acTitle || "").toLowerCase();
        break;

      case ACTION_SORT_COLUMNS.TYPE:
        // Ordre de priorité pour les types d'actions
        const actionTypePriority = {
          LUNICLEAN: 1,
          LUNISHOWER: 2,
          CUSTOM: 3,
          REVIEW: 4,
          LIMEAT: 5,
          HEATING: 6,
          YESNO: 7,
          YESNOSTAY: 8,
          CLAIM: 9,
          PROOF: 10,
          YESNOMAIL: 11,
          QUIZ: 12,
          LINK: 13,
        };
        aValue = actionTypePriority[a.acType] || 999;
        bValue = actionTypePriority[b.acType] || 999;
        break;

      case ACTION_SORT_COLUMNS.GREEN_MILES:
        aValue = a.acGreenMile || 0;
        bValue = b.acGreenMile || 0;
        break;

      case ACTION_SORT_COLUMNS.USAGE_COUNT:
        aValue = a.usageCount || 0;
        bValue = b.usageCount || 0;
        break;

      case ACTION_SORT_COLUMNS.UNIQUE_USERS:
        aValue = a.uniqueUsers || 0;
        bValue = b.uniqueUsers || 0;
        break;

      case ACTION_SORT_COLUMNS.TOTAL_GREENMILES:
        aValue = a.totalGreenmiles || 0;
        bValue = b.totalGreenmiles || 0;
        break;

      case ACTION_SORT_COLUMNS.POSITION:
        aValue = a.acPosition || 999;
        bValue = b.acPosition || 999;
        break;

      case ACTION_SORT_COLUMNS.VISIBLE:
        aValue = a.acVisible ? 1 : 0;
        bValue = b.acVisible ? 1 : 0;
        break;

      default:
        return 0;
    }

    // Tri standard pour les valeurs numériques et textuelles
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }
  });

  return sortedActions;
};

// ===== NOUVELLES FONCTIONS POUR LE TRI DES REWARDS =====

// Colonnes de tri disponibles pour les rewards
export const REWARD_SORT_COLUMNS = {
  NAME: "display_name",
  TYPE: "rwType",
  COST: "rwCost",
  USAGE_COUNT: "usageCount",
  UNIQUE_USERS: "uniqueUsers",
  TOTAL_SPENT: "totalGreenmilesSpent",
  POSITION: "rwPosition",
  VISIBLE: "rwVisible",
};

/**
 * Trie les rewards selon la colonne et direction spécifiées
 */
export const sortRewards = (rewards, sortColumn, sortDirection) => {
  const sortedRewards = [...rewards].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case REWARD_SORT_COLUMNS.NAME:
        aValue = (a.display_name || a.rwName || "").toLowerCase();
        bValue = (b.display_name || b.rwName || "").toLowerCase();
        break;

      case REWARD_SORT_COLUMNS.TYPE:
        // Ordre de priorité pour les types de rewards
        const rewardTypePriority = { CODE: 1, EAU: 2, SLIDER: 3 };
        aValue = rewardTypePriority[a.rwType] || 999;
        bValue = rewardTypePriority[b.rwType] || 999;
        break;

      case REWARD_SORT_COLUMNS.COST:
        aValue = a.rwCost || 0;
        bValue = b.rwCost || 0;
        break;

      case REWARD_SORT_COLUMNS.USAGE_COUNT:
        aValue = a.usageCount || 0;
        bValue = b.usageCount || 0;
        break;

      case REWARD_SORT_COLUMNS.UNIQUE_USERS:
        aValue = a.uniqueUsers || 0;
        bValue = b.uniqueUsers || 0;
        break;

      case REWARD_SORT_COLUMNS.TOTAL_SPENT:
        aValue = a.totalGreenmilesSpent || 0;
        bValue = b.totalGreenmilesSpent || 0;
        break;

      case REWARD_SORT_COLUMNS.POSITION:
        aValue = a.rwPosition || 999;
        bValue = b.rwPosition || 999;
        break;

      case REWARD_SORT_COLUMNS.VISIBLE:
        aValue = a.rwVisible ? 1 : 0;
        bValue = b.rwVisible ? 1 : 0;
        break;

      default:
        return 0;
    }

    // Tri standard pour les valeurs numériques et textuelles
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    }
  });

  return sortedRewards;
};

/**
 * Obtient la prochaine direction de tri pour actions/rewards
 */
export const getActionRewardSortDirection = (currentColumn, newColumn, currentDirection) => {
  if (currentColumn !== newColumn) {
    return "asc";
  }
  return currentDirection === "asc" ? "desc" : "asc";
};

/**
 * Obtient les noms affichables des types d'actions
 */
export const getActionTypeName = (type) => {
  const names = {
    LUNICLEAN: "LuniClean",
    LUNISHOWER: "LuniShower",
    CUSTOM: "Action personnalisée",
    REVIEW: "Avis",
    LIMEAT: "Limeat",
    HEATING: "Chauffage",
    YESNO: "Oui/Non",
    YESNOSTAY: "Oui/Non séjour",
    CLAIM: "Réclamation",
    PROOF: "Preuve",
    YESNOMAIL: "Oui/Non email",
    QUIZ: "Quiz",
    LINK: "Lien",
    SKIP_SERVICE: "Skip Service",
  };
  return names[type] || type;
};

/**
 * Obtient les noms affichables des types de rewards
 */
export const getRewardTypeName = (type) => {
  const names = {
    CODE: "Code promo",
    EAU: "Don d'eau",
    SLIDER: "Slider",
  };
  return names[type] || type;
};

// ===== NOUVELLES FONCTIONS POUR LE FILTRAGE PAR TYPE =====

/**
 * Obtient les types d'actions disponibles avec leurs informations
 */
export const getAvailableActionTypes = (actions) => {
  const typeCounts = {};

  // Compter les occurrences de chaque type
  actions.forEach((action) => {
    const type = action.acType;
    if (type) {
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    }
  });

  // Créer la liste des types avec leurs informations
  const actionTypeInfos = {
    LUNICLEAN: { label: "LuniClean" },
    LUNISHOWER: { label: "LuniShower" },
    CUSTOM: { label: "Action personnalisée" },
    REVIEW: { label: "Avis" },
    LIMEAT: { label: "Limeat" },
    HEATING: { label: "Chauffage" },
    YESNO: { label: "Oui/Non" },
    YESNOSTAY: { label: "Oui/Non séjour" },
    CLAIM: { label: "Réclamation" },
    PROOF: { label: "Preuve" },
    YESNOMAIL: { label: "Oui/Non email" },
    QUIZ: { label: "Quiz" },
    LINK: { label: "Lien" },
    SKIP_SERVICE: { label: "Skip Service" },
  };

  return Object.keys(typeCounts)
    .map((type) => ({
      value: type,
      label: actionTypeInfos[type]?.label || type,
      count: typeCounts[type],
    }))
    .sort((a, b) => b.count - a.count); // Trier par usage décroissant (plus utilisé en premier)
};

/**
 * Obtient les types de rewards disponibles avec leurs informations
 */
export const getAvailableRewardTypes = (rewards) => {
  const typeCounts = {};

  // Compter les occurrences de chaque type
  rewards.forEach((reward) => {
    const type = reward.rwType;
    if (type) {
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    }
  });

  // Créer la liste des types avec leurs informations
  const rewardTypeInfos = {
    CODE: { label: "Code promo", icon: "🎫" },
    EAU: { label: "Don d'eau", icon: "💧" },
    SLIDER: { label: "Slider", icon: "🎚️" },
  };

  return Object.keys(typeCounts)
    .map((type) => ({
      value: type,
      label: rewardTypeInfos[type]?.label || type,
      icon: rewardTypeInfos[type]?.icon || "🎁",
      count: typeCounts[type],
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Filtre les actions selon les types sélectionnés
 */
export const filterActionsByType = (actions, selectedTypes) => {
  if (!selectedTypes || selectedTypes.length === 0) {
    return [];
  }

  return actions.filter((action) => selectedTypes.includes(action.acType));
};

/**
 * Filtre les rewards selon les types sélectionnés
 */
export const filterRewardsByType = (rewards, selectedTypes) => {
  if (!selectedTypes || selectedTypes.length === 0) {
    return [];
  }

  return rewards.filter((reward) => selectedTypes.includes(reward.rwType));
};

/**
 * Obtient tous les types d'actions uniques
 */
export const getAllActionTypes = (actions) => {
  const types = new Set();
  actions.forEach((action) => {
    if (action.acType) {
      types.add(action.acType);
    }
  });
  return Array.from(types);
};

/**
 * Obtient tous les types de rewards uniques
 */
export const getAllRewardTypes = (rewards) => {
  const types = new Set();
  rewards.forEach((reward) => {
    if (reward.rwType) {
      types.add(reward.rwType);
    }
  });
  return Array.from(types);
};

/**
 * Formate un temps en secondes en format lisible
 * @param {number} seconds - Le temps en secondes
 * @returns {string} Le temps formaté
 */
export const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return "0s";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  if (remainingSeconds === 0) {
    return `${minutes}min`;
  }

  return `${minutes}min ${remainingSeconds}s`;
};

// ===== Résolution d'URL de démo (local / préprod / prod) =====
export const resolveDemoBaseUrl = () => {
  const env = (process.env.REACT_APP_ENV || "").toLowerCase();
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const host = typeof window !== "undefined" ? window.location?.hostname || "" : "";

  const localUrl = process.env.REACT_APP_DEMO_BASE_URL_LOCAL || "http://localhost:3200";
  const stagingUrl = process.env.REACT_APP_DEMO_BASE_URL_STAGING || "https://miles-staging.luniwave.com";
  const prodUrl = process.env.REACT_APP_DEMO_BASE_URL_PROD || "https://miles.luniwave.com";

  // Ordre de décision: variable d'env explicite > heuristique API_URL/hostname
  if (env === "local") return localUrl;
  if (env === "staging" || env === "preprod" || env === "pre-prod") return stagingUrl;
  if (env === "prod" || env === "production") return prodUrl;

  // Heuristique via hostname/API_URL
  if (host === "localhost" || host === "127.0.0.1" || /localhost/i.test(apiUrl)) return localUrl;
  if (/staging|preprod|pre-prod|dev/i.test(apiUrl) || /staging|preprod|pre-prod|dev/i.test(host)) return stagingUrl;
  return prodUrl;
};

// ===== Résolution d'URL GreenMiles (local / préprod / prod) =====
export const resolveGreenMilesBaseUrl = () => {
  const env = (process.env.REACT_APP_ENV || "").toLowerCase();
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const host = typeof window !== "undefined" ? window.location?.hostname || "" : "";

  const localUrl = process.env.REACT_APP_GREENMILES_BASE_URL_LOCAL || "http://localhost:3200";
  const stagingUrl = process.env.REACT_APP_GREENMILES_BASE_URL_STAGING || "https://miles-staging.luniwave.com";
  const prodUrl = process.env.REACT_APP_GREENMILES_BASE_URL_PROD || "https://miles.luniwave.com";

  // Ordre de décision: variable d'env explicite > heuristique API_URL/hostname
  if (env === "local") return localUrl;
  if (env === "staging" || env === "preprod" || env === "pre-prod") return stagingUrl;
  if (env === "prod" || env === "production") return prodUrl;

  // Heuristique via hostname/API_URL
  if (host === "localhost" || host === "127.0.0.1" || /localhost/i.test(apiUrl)) return localUrl;
  if (/staging|preprod|pre-prod|dev/i.test(apiUrl) || /staging|preprod|pre-prod|dev/i.test(host)) return stagingUrl;
  return prodUrl;
};
